import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import usePaginatedRequest from 'hooks/usePaginatedRequest';
import api from 'api';
import Table from 'components/Table';
import { columns, contentTransformations, headingTranslations } from './data';
import Pagination from 'components/Pagination';
import { USEERS_ROUTER_LIMIT } from '../../Users.router';
import CurrentUserContext from 'providers/CurrentUser';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button } from 'ncoded-component-library';
import AddUserModal from '../../components/AddUserModal';

import './Admins.styles.scss';
import './Admins.styles.responsive.scss';

type AdminsProps = {
  className?: string;
};

const Admins: React.FC<AdminsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { params, setQueryParam } = useQueryParams();
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const request = useMemo(() => api.user.admins.getAdmins, []);

  const {
    totalPages,
    isLoading,
    items: admins,
    setItems,
  } = usePaginatedRequest({
    getItems: request,
    defaultParams: { limit: USEERS_ROUTER_LIMIT },
    dependencies: [params, request],
  });

  const filteredColumns = useMemo(
    () => columns.filter((c) => c !== 'delete' || isSuperAdmin),
    [isSuperAdmin],
  );
  const classes = classNames('admins', className);

  return (
    <div className={classes}>
      <div className="users__heading">
        <p className="fm-page-title user-title">{t('admins')}</p>
        <Button onClick={() => modal.open()}>{t('addAdmin')}</Button>
      </div>

      <Table
        data={admins}
        columns={filteredColumns}
        headingTranslations={headingTranslations}
        isDataLoading={isLoading}
        updateTableData={setItems}
        tableContentTransformations={contentTransformations}
      />
      <Pagination
        lastPage={+totalPages}
        currentPage={+params.page || 1}
        setPage={(n) => setQueryParam('page', n)}
      />
      <AddUserModal
        ref={modalRef}
        addUserRequest={api.user.admins.addAdmin}
        onSuccess={setItems}
      />
    </div>
  );
};

export default Admins;
